import React, { useState } from 'react';
import { IconButton, Tooltip, Button } from '@material-ui/core';
import downloadIcon from '../../assets/download.svg';
import StepConfirmation, { ConfirmationDialogProps } from './StepConfirmation';
import { Alert } from '@material-ui/lab';

export interface CSVDownloadButtonProps {
  /**
   * Function to generate CSV content
   * Should return the complete CSV string including headers
   */
  generateCSVContent: () => string;
  
  /**
   * Optional filename for the downloaded file (without .csv extension)
   * Defaults to 'download'
   */
  filename?: string;
  
  /**
   * Tooltip text for the button
   */
  tooltipText?: string;
  
  /**
   * Whether the button is disabled
   */
  disabled?: boolean;
  
  /**
   * Optional validation function to check if download is allowed
   * Should return undefined if valid, or error message if invalid
   */
  validator?: () => string | undefined;
  
  /**
   * Size of the button
   */
  size?: 'small' | 'medium';
  
  /**
   * Additional styles for the button
   */
  style?: React.CSSProperties;
  
  /**
   * Optional label to display to the left of the icon
   */
  label?: string;
}

export const CSVDownloadButton: React.FC<CSVDownloadButtonProps> = ({
  generateCSVContent,
  filename = 'download',
  tooltipText = 'Download CSV',
  disabled = false,
  validator,
  size = 'medium',
  style,
  label
}) => {
  const [dialogProps, setDialogProps] = useState<ConfirmationDialogProps>();

  const handleDownload = () => {
    // Run validation if provided
    if (validator) {
      const validationError = validator();
      if (validationError) {
        showError(validationError);
        return;
      }
    }

    try {
      // Generate CSV content
      const csvContent = generateCSVContent();
      
      // Create and download the file with UTF-8 BOM
      const bom = '\uFEFF'; // UTF-8 BOM
      const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating CSV:', error);
      showError('Failed to generate CSV file');
    }
  };

  const showError = (message: string) => {
    setDialogProps({
      title: 'CSV Download Error',
      content: <Alert severity="error" style={{ marginBottom: 16, whiteSpace: 'pre-line' }}>
        {message}
        </Alert>,
      onClose: () => setDialogProps(undefined),
      closeLabel: 'OK'
    });
  };

  const iconElement = (
    <img 
      src={downloadIcon} 
      alt="CSV Download" 
      style={{ 
        width: size === 'small' ? '20px' : '24px',
        height: size === 'small' ? '20px' : '24px',
        filter: disabled ? 'grayscale(100%) opacity(50%)' : 'none'
      }} 
    />
  );

  return (
    <>
      <Tooltip title={tooltipText}>
        {label ? (
          <Button
            onClick={handleDownload}
            disabled={disabled}
            size={size}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              ...style 
            }}
          >
            {iconElement}
            {label}
          </Button>
        ) : (
          <IconButton
            onClick={handleDownload}
            disabled={disabled}
            size={size}
            style={style}
          >
            {iconElement}
          </IconButton>
        )}
      </Tooltip>
      
      {dialogProps && (
        <StepConfirmation
          confirmationDialogs={[dialogProps]}
          finish={() => { }}
          cancel={() => setDialogProps(undefined)}
          isValid={true}
        />
      )}
    </>
  );
}; 